import { useState, useCallback } from 'react';

interface ShareOptions {
  title: string;
  text: string;
  url: string;
}

interface UseShareReturn {
  share: (options: ShareOptions) => Promise<boolean>;
  copied: boolean;
  canNativeShare: boolean;
}

export function useShare(): UseShareReturn {
  const [copied, setCopied] = useState(false);
  
  const canNativeShare = typeof navigator !== 'undefined' && 'share' in navigator;
  
  const share = useCallback(async (options: ShareOptions): Promise<boolean> => {
    // Try native share first (mobile)
    if (canNativeShare) {
      try {
        await navigator.share(options);
        return true;
      } catch (err) {
        // User cancelled or share failed - fall back to clipboard
        if ((err as Error).name === 'AbortError') {
          // User cancelled, don't fall back
          return false;
        }
      }
    }
    
    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(options.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return true;
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      return false;
    }
  }, [canNativeShare]);
  
  return { share, copied, canNativeShare };
}
